import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import api from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [store, setStore] = useState(null);
  const [stores, setStores] = useState([]);
  const [selectedStoreId, setSelectedStoreId] = useState("all");
  const [loading, setLoading] = useState(true);

  const loadStores = useCallback(async () => {
    try {
      const { data } = await api.get("/stores");
      setStores(data);
    } catch (e) {
      console.error("Failed to load stores:", e);
    }
  }, []);

  // Session lives in an httpOnly cookie — restore it via /auth/me (401 = not logged in)
  const bootstrap = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data.user);
      setStore(data.store);
      await loadStores();
    } catch (e) {
      // 401 on cold load = no active session (expected); log anything else.
      if (e?.response?.status !== 401) console.warn("Session bootstrap failed:", e);
    }
    setLoading(false);
  }, [loadStores]);

  useEffect(() => {
    bootstrap();
  }, [bootstrap]);

  const login = useCallback(async (usernameOrPayload, password) => {
    // Backward-compat: accept either (username, password) or a single payload object
    // (needed for 2FA flow that also passes totp_code + trust_device).
    const payload = typeof usernameOrPayload === "string"
      ? { username: usernameOrPayload, password }
      : usernameOrPayload;
    const { data } = await api.post("/auth/login", payload);
    // Auth is 100% cookie-based — the backend no longer returns a raw JWT in the response.
    // We just hydrate the user + store from the payload; subsequent requests carry the
    // httpOnly cookie automatically.
    setUser(data.user);
    setStore(data.store);
    await loadStores();
    return data.user;
  }, [loadStores]);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch (e) {
      console.warn("Logout request failed:", e);
    }
    setUser(null);
    setStore(null);
    setStores([]);
  }, []);

  const isAdmin = user?.role === "admin";
  const activeStoreId = isAdmin ? (selectedStoreId === "all" ? null : selectedStoreId) : user?.store_id;

  const value = useMemo(() => ({
    user, store, stores, loading, login, logout, isAdmin,
    selectedStoreId, setSelectedStoreId, activeStoreId, loadStores,
  }), [user, store, stores, loading, login, logout, isAdmin, selectedStoreId, activeStoreId, loadStores]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
